import React from 'react';
import { Field } from 'react-final-form';

// Import config and utils
import { useIntl } from '../../util/reactIntl';
import {
  SCHEMA_TYPE_ENUM,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_LONG,
  SCHEMA_TYPE_BOOLEAN,
  SCHEMA_TYPE_YOUTUBE,
} from '../../util/types';
import {
  required,
  nonEmptyArray,
  validateInteger,
  validateYoutubeURL,
} from '../../util/validators';
import { LISTING_MESSAGE_MAX_LENGTH, SEAT_FIELD_MAX_LENGTH } from '../../util/characterLimits';
// Import shared components
import { FieldCheckboxGroup, FieldSelect, FieldTextInput, FieldBoolean } from '../../components';
// Import modules from this directory
import css from './CustomExtendedDataField.module.css';

const EVENT_DATE_TIME = 'pub_eventDateTime';
const VENUE_NAME = 'pub_venueName';
const VENUE_LOCATION = 'pub_venueLocation';
const SEAT_SECTION = 'pub_seatSection';
const SEAT_ROW = 'pub_seatRow';
const SEAT_NUMBER = 'pub_seatNumber';

const disabledFields = [EVENT_DATE_TIME, VENUE_NAME, VENUE_LOCATION];
const singleTextFields = [
  EVENT_DATE_TIME,
  VENUE_NAME,
  VENUE_LOCATION,
  SEAT_SECTION,
  SEAT_ROW,
  SEAT_NUMBER,
];

const createFilterOptions = options => options.map(o => ({ key: `${o.option}`, label: o.label }));

const getLabel = fieldConfig => fieldConfig?.saveConfig?.label || fieldConfig?.label;

const CustomFieldEnum = props => {
  const {
    name,
    fieldConfig,
    defaultRequiredMessage,
    formId,
    intl,
    disabled,
    isBidListing,
    isTicketSaleListing,
  } = props;
  const { enumOptions = [], saveConfig, key } = fieldConfig || {};
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};
  const placeholder =
    placeholderMessage ||
    intl.formatMessage({ id: 'CustomExtendedDataField.placeholderSingleSelect' });
  const filterOptions = createFilterOptions(enumOptions);

  const label = getLabel(fieldConfig);

  // Check if this is the ticket_type field that needs conditional text field
  const isTicketTypeField = key === 'ticket_type' || name === 'pub_ticket_type';

  return filterOptions ? (
    <>
      <FieldSelect
        className={css.customField}
        name={name}
        id={formId ? `${formId}.${name}` : name}
        label={label}
        {...validateMaybe}
        disabled={disabled}
      >
        <option disabled value="">
          {placeholder}
        </option>
        {filterOptions.map(optionConfig => {
          const optionKey = optionConfig.key;
          return (
            <option key={optionKey} value={optionKey}>
              {optionConfig.label}
            </option>
          );
        })}
      </FieldSelect>

      {/* Conditional text field for ticket_type when "Specific seating (see details)" is selected */}
      {/* Hide this field for bid listings and ticket sales since they have dedicated details fields */}
      {isTicketTypeField && !isBidListing && !isTicketSaleListing && (
        <Field name={name} subscription={{ value: true }}>
          {({ input: { value } }) => {
            // Find the selected option to check its label
            const selectedOption = filterOptions.find(opt => opt.key === value);
            const isSpecificSelected =
              selectedOption &&
              (selectedOption.label?.toLowerCase().includes('specific') ||
                value === 'specific' ||
                value?.toLowerCase().includes('specific'));

            if (!isSpecificSelected || !value) {
              return null;
            }

            // Field name for the seat details text field
            const seatDetailsFieldName =
              name === 'pub_ticket_type' ? 'pub_seat_details' : 'seat_details';

            return (
              <FieldTextInput
                className={css.customField}
                id={formId ? `${formId}.${seatDetailsFieldName}` : seatDetailsFieldName}
                name={seatDetailsFieldName}
                type="textarea"
                label={intl.formatMessage({
                  id: 'CustomExtendedDataField.seatDetailsLabel',
                  defaultMessage: 'Seat preferences details',
                })}
                placeholder={intl.formatMessage({
                  id: 'CustomExtendedDataField.seatDetailsPlaceholder',
                  defaultMessage:
                    'Enter your specific seat preferences (e.g., section, row, area preferences)',
                })}
              />
            );
          }}
        </Field>
      )}
    </>
  ) : null;
};

const CustomFieldMultiEnum = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, disabled } = props;
  const { enumOptions = [], saveConfig } = fieldConfig || {};
  const { isRequired, requiredMessage } = saveConfig || {};
  const label = getLabel(fieldConfig);
  const validateMaybe = isRequired
    ? { validate: nonEmptyArray(requiredMessage || defaultRequiredMessage) }
    : {};

  return enumOptions ? (
    <FieldCheckboxGroup
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={label}
      options={createFilterOptions(enumOptions)}
      {...validateMaybe}
      disabled={disabled}
    />
  ) : null;
};

const CustomFieldText = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl, isPublished } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const { key } = fieldConfig || {};
  const label = getLabel(fieldConfig);
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};

  // Special placeholder for message field (buyer's message)
  const isMessageField = key === 'message' || name === 'pub_message';
  let placeholder;
  if (isMessageField) {
    placeholder = intl.formatMessage({
      id: 'CustomExtendedDataField.messagePlaceholder',
      defaultMessage:
        'Enter any additional details (e.g. seating preferences or important details about your offer)',
    });
  } else {
    placeholder =
      placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderText' });
  }

  const disabled = isPublished ? true : disabledFields.includes(name);
  const isSingleText = singleTextFields.includes(name);
  const maxLengthMaybe = isMessageField
    ? { maxLength: LISTING_MESSAGE_MAX_LENGTH }
    : [SEAT_SECTION, SEAT_ROW, SEAT_NUMBER].includes(name)
    ? { maxLength: SEAT_FIELD_MAX_LENGTH }
    : {};

  // Format datetime fields to display as "Jan 30, 2026, 7:00 PM"
  const isDateTimeField = name === EVENT_DATE_TIME;

  const formatDateTime = value => {
    if (!value) return value;
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return value;

      // Get month name (short form)
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const month = months[date.getMonth()];

      // Get day
      const day = date.getDate();

      // Get year
      const year = date.getFullYear();

      // Get hours and convert to 12-hour format
      let hours = date.getHours();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12; // 0 should be 12

      // Get minutes with leading zero if needed
      const minutes = date
        .getMinutes()
        .toString()
        .padStart(2, '0');

      // Format: "Jan 30, 2026, 7:00 PM"
      return `${month} ${day}, ${year}, ${hours}:${minutes} ${ampm}`;
    } catch (e) {
      return value;
    }
  };

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type={isSingleText ? 'text' : 'textarea'}
      label={label}
      placeholder={placeholder}
      {...validateMaybe}
      {...maxLengthMaybe}
      disabled={disabled}
      format={isDateTimeField ? formatDateTime : undefined}
    />
  );
};

const CustomFieldLong = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl, disabled } = props;
  const { minimum, maximum, saveConfig, key } = fieldConfig;
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};
  const label = getLabel(fieldConfig);

  // Special validation for number_of_tickets field: must be 1-10, not 0
  const isNumberOfTicketsField = key === 'number_of_tickets' || name === 'pub_number_of_tickets';
  const placeholder = isNumberOfTicketsField
    ? intl.formatMessage({
        id: 'CustomExtendedDataField.numberOfTicketsPlaceholder',
        defaultMessage: 'Enter a number between 1 - 10',
      })
    : placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderLong' });

  const effectiveMin = isNumberOfTicketsField ? 1 : minimum;
  const effectiveMax = isNumberOfTicketsField ? 10 : maximum;

  const numberTooSmallMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooSmall' },
    { min: effectiveMin }
  );
  const numberTooBigMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooBig' },
    { max: effectiveMax }
  );

  // Field with schema type 'long' will always be validated against min & max
  const validate = (value, min, max) => {
    const requiredMsg = requiredMessage || defaultRequiredMessage;
    if (isRequired && value == null) {
      return requiredMsg;
    }
    // For number_of_tickets: always required (even if config says optional) so bid form blocks submit when empty
    if (isNumberOfTicketsField) {
      if (value == null || value === '' || (typeof value === 'number' && isNaN(value))) {
        return intl.formatMessage({
          id: 'CustomExtendedDataField.numberOfTicketsError',
          defaultMessage: 'Select a number between 1 - 10',
        });
      }
      if (value === 0 || value < 1 || value > 10) {
        return intl.formatMessage({
          id: 'CustomExtendedDataField.numberOfTicketsError',
          defaultMessage: 'Select a number between 1 - 10',
        });
      }
    }
    return validateInteger(value, max, min, numberTooSmallMessage, numberTooBigMessage);
  };

  // Format function to ensure no leading zeros are displayed
  const formatValue = value => {
    if (value == null || value === '') {
      return '';
    }
    // Convert to integer and back to string to remove leading zeros
    const num = Number.parseInt(value, 10);
    if (Number.isNaN(num)) {
      return '';
    }
    return String(num);
  };

  // Parse function to handle input sanitization for number_of_tickets
  const parseValue = value => {
    if (value == null || value === '') {
      return null;
    }

    // For number_of_tickets field, prevent decimals and leading zeros
    if (isNumberOfTicketsField) {
      // Remove any non-digit characters (including decimals, negative signs, etc.)
      let sanitized = String(value).replace(/[^0-9]/g, '');

      // Remove leading zeros
      sanitized = sanitized.replace(/^0+/, '') || '';

      // If empty after sanitization, return null
      if (sanitized === '') {
        return null;
      }

      // Limit to 2 digits max (since max is 10)
      if (sanitized.length > 2) {
        sanitized = sanitized.slice(0, 2);
      }

      // Parse as integer
      const parsed = Number.parseInt(sanitized, 10);

      // Return parsed value (validation will catch if out of range)
      return Number.isNaN(parsed) ? null : parsed;
    }

    // For other fields, use standard parsing
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  };

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type={isNumberOfTicketsField ? 'text' : 'number'}
      step="1"
      min={isNumberOfTicketsField ? 1 : minimum}
      max={isNumberOfTicketsField ? 10 : maximum}
      format={isNumberOfTicketsField ? formatValue : undefined}
      parse={parseValue}
      label={label}
      placeholder={placeholder}
      validate={value => validate(value, effectiveMin, effectiveMax)}
      onKeyDown={
        isNumberOfTicketsField
          ? e => {
              // Prevent decimal point, minus sign, 'e', 'E', '+', and comma keys
              if (
                e.key === '.' ||
                e.key === '-' ||
                e.key === 'e' ||
                e.key === 'E' ||
                e.key === '+' ||
                e.key === ','
              ) {
                e.preventDefault();
                return;
              }
              // Prevent leading zero if trying to type 0 at the start of a non-empty field
              if (e.key === '0' && e.target.selectionStart === 0 && e.target.value !== '') {
                e.preventDefault();
                return;
              }
            }
          : undefined
      }
      onWheel={e => {
        // fix: number input should not change value on scroll
        if (e.target === document.activeElement) {
          // Prevent the input value change, because we prefer page scrolling
          e.target.blur();

          // Refocus immediately, on the next tick (after the current function is done)
          setTimeout(() => {
            e.target.focus();
          }, 0);
        }
      }}
      disabled={disabled}
    />
  );
};

const CustomFieldBoolean = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = getLabel(fieldConfig);
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderBoolean' });

  return (
    <FieldBoolean
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={label}
      placeholder={placeholder}
      {...validateMaybe}
    />
  );
};

const CustomFieldYoutube = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = getLabel(fieldConfig);
  const placeholder =
    placeholderMessage ||
    intl.formatMessage({ id: 'CustomExtendedDataField.placeholderYoutubeVideoURL' });

  const notValidUrlMessage = intl.formatMessage({
    id: 'CustomExtendedDataField.notValidYoutubeVideoURL',
  });

  const validate = value => {
    const requiredMsg = requiredMessage || defaultRequiredMessage;
    return isRequired && value == null
      ? requiredMsg
      : validateYoutubeURL(value, notValidUrlMessage);
  };

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="text"
      label={label}
      placeholder={placeholder}
      validate={value => validate(value)}
    />
  );
};

/**
 * Return Final Form field for each configuration according to schema type.
 *
 * These custom extended data fields are for generating input fields from configuration defined
 * in marketplace-custom-config.js. Other panels in EditListingWizard might add more extended data
 * fields (e.g. shipping fee), but these are independently customizable.
 *
 * @param {Object} props should contain fieldConfig that defines schemaType, enumOptions?, and
 * saveConfig for the field.
 */
const CustomExtendedDataField = props => {
  const intl = useIntl();
  const { enumOptions = [], schemaType } = props?.fieldConfig || {};
  const renderFieldComponent = (FieldComponent, props) => <FieldComponent {...props} intl={intl} />;

  return schemaType === SCHEMA_TYPE_ENUM && enumOptions
    ? renderFieldComponent(CustomFieldEnum, props)
    : schemaType === SCHEMA_TYPE_MULTI_ENUM && enumOptions
    ? renderFieldComponent(CustomFieldMultiEnum, props)
    : schemaType === SCHEMA_TYPE_TEXT
    ? renderFieldComponent(CustomFieldText, props)
    : schemaType === SCHEMA_TYPE_LONG
    ? renderFieldComponent(CustomFieldLong, props)
    : schemaType === SCHEMA_TYPE_BOOLEAN
    ? renderFieldComponent(CustomFieldBoolean, props)
    : schemaType === SCHEMA_TYPE_YOUTUBE
    ? renderFieldComponent(CustomFieldYoutube, props)
    : null;
};

export default CustomExtendedDataField;
